import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "./DashboardLayout";
import { OrderDrawer } from "./OrderDrawer";
import { api, formatDateMX, MXN } from "../../lib/apiClient";
import { ORDER_STATUSES, PAYMENT_STATUSES, statusMeta } from "../../lib/constants";
import { Search } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

export default function Pedidos() {
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [date, setDate] = useState("");
  const [params, setParams] = useSearchParams();
  const openId = params.get("open");

  const load = useCallback(() => {
    const p = {};
    if (search) p.search = search;
    if (status) p.status = status;
    if (payment) p.payment_status = payment;
    if (date) p.date = date;
    api.get("/orders", { params: p }).then((r) => setOrders(r.data)).catch(() => {});
  }, [search, status, payment, date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  useAdminRealtime(load, ["orders"]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const open = (id) => setParams({ open: id });
  const close = () => setParams({});

  return (
    <DashboardLayout title="Pedidos">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_auto_auto_auto] gap-3 mb-5 min-w-0">
        <div className="relative sm:col-span-2 xl:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rosa" />
          <input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre, teléfono o folio…"
            className="w-full bg-blancoCalido border-2 border-beige rounded-2xl sm:rounded-full pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-vino" />
        </div>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-blancoCalido border-2 border-beige rounded-2xl sm:rounded-full px-4 py-3 text-sm focus:outline-none focus:border-vino">
          <option value="">Todos los estados</option>
          {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select data-testid="filter-payment" value={payment} onChange={(e) => setPayment(e.target.value)} className="w-full bg-blancoCalido border-2 border-beige rounded-2xl sm:rounded-full px-4 py-3 text-sm focus:outline-none focus:border-vino">
          <option value="">Todos los pagos</option>
          {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input type="date" data-testid="filter-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full bg-blancoCalido border-2 border-beige rounded-2xl sm:rounded-full px-4 py-3 text-sm focus:outline-none focus:border-vino" />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-blancoCalido border border-beige rounded-[24px] overflow-hidden shadow-pastel">
        <table className="w-full text-sm">
          <thead className="bg-beige/60 text-vino"><tr>
            {["Folio", "Cliente", "Producto", "Fecha", "Estado", "Pago", "Total"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {orders.map((o) => {
              const m = statusMeta(o.status);
              return (
                <tr key={o.id} onClick={() => open(o.id)} data-testid={`order-row-${o.id}`} className="border-t border-beige cursor-pointer hover:bg-beige/30">
                  <td className="px-4 py-3 font-medium text-vino">{o.folio}</td>
                  <td className="px-4 py-3">{o.customer_name}<br /><span className="text-xs text-cafe/50">{o.customer_phone}</span></td>
                  <td className="px-4 py-3">{o.product_name}</td>
                  <td className="px-4 py-3">{formatDateMX(o.delivery_date)}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2.5 py-1 rounded-full text-white" style={{ background: m.color }}>{m.label}</span></td>
                  <td className="px-4 py-3 text-xs">{PAYMENT_STATUSES.find((p) => p.value === o.payment_status)?.label}</td>
                  <td className="px-4 py-3">{MXN(o.final_price)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {orders.length === 0 && <p className="p-6 text-center text-cafe/50 text-sm">Sin pedidos.</p>}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {orders.map((o) => {
          const m = statusMeta(o.status);
          return (
            <button key={o.id} onClick={() => open(o.id)} data-testid={`order-card-${o.id}`} className="w-full min-w-0 text-left bg-blancoCalido border border-beige rounded-[24px] p-4 shadow-pastel active:scale-[0.99] transition">
              <div className="flex justify-between items-start gap-3 min-w-0"><p className="font-medium text-vino truncate">{o.folio}</p><span className="shrink-0 text-xs px-2.5 py-1 rounded-full text-white" style={{ background: m.color }}>{m.label}</span></div>
              <p className="text-sm text-cafe mt-2 font-medium">{o.customer_name}</p>
              <p className="text-sm text-cafe/65 mt-0.5">{o.product_name}</p>
              <div className="mt-3 grid grid-cols-1 min-[360px]:grid-cols-3 gap-2 text-center text-xs">
                <span className="bg-crema border border-beige rounded-xl py-2">{formatDateMX(o.delivery_date)}</span>
                <span className="bg-crema border border-beige rounded-xl py-2">{o.delivery_time || "-"}</span>
                <span className="bg-crema border border-beige rounded-xl py-2 text-vino font-medium">{MXN(o.final_price)}</span>
              </div>
            </button>
          );
        })}
        {orders.length === 0 && <p className="text-center text-cafe/50 text-sm py-8">Sin pedidos.</p>}
      </div>

      {openId && <OrderDrawer orderId={openId} settings={settings} onClose={close} onUpdated={load} />}
    </DashboardLayout>
  );
}
